import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppWindow,
  CheckCircle2,
  Edit,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type ExternalBase44AppStatus = "unknown" | "connected" | "needs_reconnect" | "invalid";

type ExternalBase44AppValidationResult = {
  checkedAt?: string | null;
  status?: "ok" | "skipped" | "tool_missing" | "failed" | string;
  reason?: string | null;
  targetSource?: string | null;
  appIdHash?: string | null;
  toolCount?: number | null;
  tools?: {
    getAppStatus?: boolean;
    getAppPreviewUrl?: boolean;
  } | null;
  appStatus?: {
    state?: string | null;
    providerStatus?: string | null;
    keys?: string[];
  } | null;
  preview?: {
    toolAvailable?: boolean;
    toolSucceeded?: boolean;
    originHash?: string | null;
    urlHash?: string | null;
    hasPreviewToken?: boolean | null;
    httpStatus?: number | null;
    hasTitle?: boolean | null;
    classification?: "ready" | "preview_loading" | "login" | "error" | "not_checked" | string;
  } | null;
};

type ExternalBase44AppRecord = {
  id: string;
  name: string;
  base44_app_id: string;
  base44_app_url: string | null;
  base44_embed_url: string | null;
  notes: string | null;
  status: ExternalBase44AppStatus;
  validation_result: ExternalBase44AppValidationResult | null;
  last_checked_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

type ExternalBase44AppsPayload = {
  ok?: boolean;
  error?: string;
  needsReconnect?: boolean;
  apps?: ExternalBase44AppRecord[];
};

type ExternalBase44AppFormState = {
  name: string;
  base44AppId: string;
  base44AppUrl: string;
  base44EmbedUrl: string;
  notes: string;
};

const STATUS_LABELS: Record<ExternalBase44AppStatus, string> = {
  unknown: "Not validated",
  connected: "Connected",
  needs_reconnect: "Reconnect needed",
  invalid: "Invalid",
};

function providerText(value?: string | null): string {
  return (value ?? "").replace(/base44/gi, "Provider");
}

function formatDate(value?: string | null): string {
  if (!value) return "Not yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
}

function statusClass(status: ExternalBase44AppStatus): string {
  if (status === "connected") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
  if (status === "needs_reconnect") return "border-amber-500/40 bg-amber-500/10 text-amber-800";
  if (status === "invalid") return "border-destructive/40 bg-destructive/10 text-destructive";
  return "border-border bg-muted text-muted-foreground";
}

function validationSummary(validationResult: ExternalBase44AppValidationResult | null | undefined): string {
  if (!validationResult) return "Not validated yet";
  if (validationResult.reason) return providerText(validationResult.reason);
  const preview = validationResult.preview?.classification;
  if (preview && preview !== "not_checked") return providerText(preview.replace(/_/g, " "));
  return "Validated";
}

function externalBase44AppStatus(record: ExternalBase44AppRecord): ExternalBase44AppStatus {
  return record.status;
}

export function ExternalBase44Apps() {
  const { toast } = useToast();
  const [apps, setApps] = useState<ExternalBase44AppRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<ExternalBase44AppRecord | null>(null);
  const [form, setForm] = useState<ExternalBase44AppFormState>({
    name: "",
    base44AppId: "",
    base44AppUrl: "",
    base44EmbedUrl: "",
    notes: "",
  });

  const summary = useMemo(() => ({
    total: apps.length,
    connected: apps.filter((app) => app.status === "connected").length,
    reconnect: apps.filter((app) => app.status === "needs_reconnect").length,
    invalid: apps.filter((app) => app.status === "invalid").length,
  }), [apps]);

  const loadApps = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<ExternalBase44AppsPayload>("base44-external-apps", {
        body: { action: "list" },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data.error ?? "Provider apps could not be loaded");
      setApps(data?.apps ?? []);
    } catch (error) {
      toast({
        title: "Unable to load external apps",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadApps();
  }, [loadApps]);

  const resetForm = () => {
    setEditingApp(null);
    setForm({
      name: "",
      base44AppId: "",
      base44AppUrl: "",
      base44EmbedUrl: "",
      notes: "",
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (app: ExternalBase44AppRecord) => {
    setEditingApp(app);
    setForm({
      name: app.name,
      base44AppId: app.base44_app_id,
      base44AppUrl: app.base44_app_url ?? "",
      base44EmbedUrl: app.base44_embed_url ?? "",
      notes: app.notes ?? "",
    });
    setDialogOpen(true);
  };

  const runAction = async (action: string, body: Record<string, unknown>, successTitle: string): Promise<boolean> => {
    setBusyAction(action);
    try {
      const { data, error } = await supabase.functions.invoke<ExternalBase44AppsPayload>("base44-external-apps", {
        body: { action, ...body },
      });
      if (error) throw error;
      if (data?.ok === false) {
        throw new Error(data.error ?? "Provider app action did not complete");
      }
      setApps(data?.apps ?? []);
      toast({ title: successTitle });
      return true;
    } catch (error) {
      toast({
        title: "Provider app action failed",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setBusyAction(null);
    }
  };

  const saveApp = async () => {
    const name = form.name.trim();
    const base44AppId = form.base44AppId.trim();
    if (!name || !base44AppId) return;

    const action = editingApp ? "update" : "create";
    const body: Record<string, unknown> = {
      id: editingApp?.id,
      name,
      base44AppId,
      base44AppUrl: form.base44AppUrl.trim() || null,
      base44EmbedUrl: form.base44EmbedUrl.trim() || null,
      notes: form.notes.trim() || null,
    };

    const saved = await runAction(action, body, editingApp ? "External app updated" : "External app created");
    if (saved) {
      setDialogOpen(false);
      resetForm();
    }
  };

  const deleteApp = async (app: ExternalBase44AppRecord) => {
    if (!window.confirm(`Delete ${app.name}? This removes the admin record only.`)) return;
    await runAction("delete", { id: app.id }, "External app deleted");
  };

  const validateApp = async (app: ExternalBase44AppRecord) => {
    await runAction("validate", { id: app.id }, "External app validated");
  };

  const validateAll = async () => {
    await runAction("validate_all", {}, "All external apps validated");
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
            <AppWindow className="h-5 w-5" />
            External Base44 Apps
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Register Base44 apps created outside Applyt and validate them against the current Provider connection.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => void loadApps()} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => void validateAll()} disabled={busyAction !== null || apps.length === 0}>
            <ShieldAlert className="mr-2 h-4 w-4" />
            Validate all
          </Button>
          <Button size="sm" onClick={openCreateDialog} disabled={busyAction !== null}>
            Add app
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Registered apps</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{summary.total}</div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Connected</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{summary.connected}</div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Reconnect needed</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{summary.reconnect}</div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground">Invalid</div>
            <div className="mt-2 text-2xl font-semibold text-foreground">{summary.invalid}</div>
          </CardContent>
        </Card>
      </div>

      {loading && apps.length === 0 && (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading external apps...
        </div>
      )}

      {!loading && apps.length === 0 && (
        <Card className="border-border/60">
          <CardContent className="space-y-3 p-6">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <TriangleAlert className="h-4 w-4 text-muted-foreground" />
              No external Base44 apps yet
            </div>
            <p className="text-sm text-muted-foreground">
              Add the app name and the Base44 app ID from the external editor URL, then validate the connection from this page.
            </p>
            <Button onClick={openCreateDialog}>Add the first app</Button>
          </CardContent>
        </Card>
      )}

      {apps.length > 0 && (
        <div className="grid gap-3">
          {apps.map((app) => {
            const status = externalBase44AppStatus(app);
            const validationResult = app.validation_result;
            return (
              <Card key={app.id} className="border-border/60">
                <CardContent className="p-4">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-base font-semibold text-foreground">{app.name}</h3>
                        <Badge variant="outline" className={cn("capitalize", statusClass(status))}>
                          {STATUS_LABELS[status]}
                        </Badge>
                        {app.base44_app_url && (
                          <a
                            href={app.base44_app_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                          >
                            App URL
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>

                      <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                        <div>
                          <dt className="text-muted-foreground">Base44 app ID</dt>
                          <dd className="truncate font-medium text-foreground">{app.base44_app_id}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Last checked</dt>
                          <dd className="font-medium text-foreground">{formatDate(app.last_checked_at)}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Last success</dt>
                          <dd className="font-medium text-foreground">{formatDate(app.last_success_at)}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Validation</dt>
                          <dd className="font-medium text-foreground">{validationSummary(validationResult)}</dd>
                        </div>
                      </dl>

                      {app.base44_embed_url && (
                        <p className="mt-3 truncate text-xs text-muted-foreground">
                          Embed URL: {app.base44_embed_url}
                        </p>
                      )}

                      {(validationResult?.appStatus || validationResult?.preview || app.last_error) && (
                        <div className="mt-4 grid gap-2 rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground sm:grid-cols-2 xl:grid-cols-4">
                          <div>
                            <div className="text-[11px] uppercase tracking-wider">Provider state</div>
                            <div className="mt-1 font-medium text-foreground">
                              {providerText(validationResult?.appStatus?.state ?? validationResult?.appStatus?.providerStatus ?? "Unknown")}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wider">Preview</div>
                            <div className="mt-1 font-medium text-foreground">
                              {validationResult?.preview?.classification
                                ? providerText(validationResult.preview.classification.replace(/_/g, " "))
                                : "Not checked"}
                              {typeof validationResult?.preview?.httpStatus === "number" ? ` (HTTP ${validationResult.preview.httpStatus})` : ""}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wider">Tools</div>
                            <div className="mt-1 font-medium text-foreground">
                              {validationResult?.toolCount ? `${validationResult.toolCount} visible` : "Unknown"}
                            </div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wider">Error</div>
                            <div className="mt-1 font-medium text-foreground">
                              {app.last_error ? providerText(app.last_error) : "None"}
                            </div>
                          </div>
                        </div>
                      )}

                      {app.notes && (
                        <p className="mt-3 text-sm text-muted-foreground">
                          {app.notes}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 xl:justify-end">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(app)} disabled={busyAction !== null}>
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void validateApp(app)}
                        disabled={busyAction !== null}
                      >
                        {busyAction === "validate" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                        Validate
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => void deleteApp(app)} disabled={busyAction !== null}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {apps.length > 0 && (
        <Alert>
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Validation uses the current Provider connection</AlertTitle>
          <AlertDescription>
            If validation reports reconnect or auth errors, fix the Provider connection first, then re-run validation for the external apps.
          </AlertDescription>
        </Alert>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingApp ? "Edit external app" : "Add external app"}</DialogTitle>
            <DialogDescription>
              Add the Base44 app ID from the external editor URL, then validate that the Provider account can reach it.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="external-app-name">Name</Label>
              <Input
                id="external-app-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="Marketing site, internal CRM, etc."
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="external-app-id">Base44 app ID</Label>
              <Input
                id="external-app-id"
                value={form.base44AppId}
                onChange={(event) => setForm((current) => ({ ...current, base44AppId: event.target.value }))}
                placeholder="Copy from /apps/{APP_ID}/editor/"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="external-app-url">App URL</Label>
              <Input
                id="external-app-url"
                value={form.base44AppUrl}
                onChange={(event) => setForm((current) => ({ ...current, base44AppUrl: event.target.value }))}
                placeholder="https://example.base44.app"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="external-app-embed-url">Embed URL</Label>
              <Input
                id="external-app-embed-url"
                value={form.base44EmbedUrl}
                onChange={(event) => setForm((current) => ({ ...current, base44EmbedUrl: event.target.value }))}
                placeholder="Optional embed or preview URL"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="external-app-notes">Notes</Label>
              <Textarea
                id="external-app-notes"
                value={form.notes}
                onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                placeholder="Optional context for operators"
                className="min-h-24"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void saveApp()} disabled={!form.name.trim() || !form.base44AppId.trim() || busyAction !== null}>
              {busyAction === "create" || busyAction === "update" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingApp ? "Save changes" : "Create app"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
